import { useEffect, useMemo } from 'react'

import settings from 'settings'

import { FeatureFlag, useFeatureFlag } from 'app/ui/useFeatureFlag'

import useGetAccountData from 'features/account/ui/common/useGetAccountData'
import { useMeUser } from 'features/users/hooks/useMeUser'

import { NPSService } from '../domain/NPSService'

/*
  REFERENCE: 
  https://docs.vitally.io/pushing-data-to-vitally/using-the-vitally-api/vitallys-javascript-sdk-vitally.js
  https://docs.vitally.io/using-vitallys-nps-surveys/installing-and-configuring-nps-surveys
*/
function buildVitallyWebSurvey(npsService: NPSService) {
  let scriptSDK = document.querySelector('#vitally-sdk') as HTMLScriptElement

  if (!scriptSDK) {
    scriptSDK = document.createElement('script')
    scriptSDK.src = 'https://cdn.vitally-eu.io/vitally.js/v1/vitally.js'
    scriptSDK.defer = true
    scriptSDK.id = 'vitally-sdk'
    document.head.appendChild(scriptSDK)

    scriptSDK.onload = () => {
      const isOpen = !!document.querySelector('.Vitally--Nps')
      if (!isOpen && npsService.shouldPromptNPS()) {
        npsService.promptNPS()
      }
    }
  }
}

export function useNPS() {
  const account = useGetAccountData()
  const user = useMeUser()
  const npsSurveyEnabled = useFeatureFlag(FeatureFlag.NPSVitallySurvey) && Boolean(settings.vitallyToken)

  const npsService = useMemo(() => {
    if (!npsSurveyEnabled || !user || !account || !settings.vitallyToken) {
      return null
    }
    return new NPSService({ user, account })
  }, [user, account, npsSurveyEnabled])

  useEffect(() => {
    if (npsService) {
      buildVitallyWebSurvey(npsService)
    }
    return () => {
      const scriptSDK = document.querySelector('#vitally-sdk')
      if (scriptSDK) document.head.removeChild(scriptSDK)
    }
  }, [npsService])
}

export default useNPS
